"use client";
import { useState } from "react";
import { format } from "date-fns";
import { convertToZone, formatCOP } from "../../utils/formatters";
import { SalesTableProps } from "./SalesInterface";
import { ButtonsFilters } from "../filters/ButtonsFilters";
import { PaidState } from "../../types/order/paidState";
import { LogisticState } from "../../types/order/logisticState";
import {
  downloadOrders,
  getOrderDetails,
  deleteOrder,
} from "../../services/orders/orderAxios";
import { useCurrency } from "../../context/Currency";
import { dictionary, Language } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";
import { DefaultButton } from "../buttons/DefaultButton";
import { PaymentProvider } from "../../pages/SalesPage/SalesPageInterface";
import { IconTrash } from "../../assets/icons/icons";
import { BaseModal } from "../modals/BaseModal";
import { handleApi } from "../../utils/handleApi";
import { Order } from "../../pages/SalesPage/SalesPageInterface";
import { toast } from "react-hot-toast";
import { DateFilter } from "./DateFilter";
import { ProductFilter } from "./ProductFilter";
import { Link, useLocation } from "react-router-dom";

interface DeleteOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: string;
  onConfirm: () => void;
  loading: boolean;
}

const DeleteOrderModal = ({
  isOpen,
  onClose,
  message,
  onConfirm,
  loading,
}: DeleteOrderModalProps) => (
  <BaseModal
    isOpen={isOpen}
    onClose={onClose}
    imageSrc="https://fluxi.s3.amazonaws.com/prod/assets/media/warning-purple.gif"
    imageAlt="Warning"
    title="Eliminar orden"
    description={message}
    buttonOne={{
      text: "Eliminar",
      onClick: onConfirm,
      loading,
      disabled: loading,
    }}
    buttonTwo={{
      text: "Cancelar",
      onClick: onClose,
    }}
  />
);

const EmptyState = ({ language }: { language: Language }) => (
  <tr>
    <td colSpan={7} className="px-6 py-12 text-center">
      <div className="flex flex-col items-center justify-center">
        <svg
          className="w-16 h-16 text-gray-300 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          {dictionary["no-orders-found"]?.[language] ||
            "No se encontraron órdenes"}
        </h3>
        <p className="text-sm text-gray-500">
          {dictionary["try-different-filters"]?.[language] ||
            "Intenta con diferentes filtros o rango de fechas"}
        </p>
      </div>
    </td>
  </tr>
);

interface OrderRowProps {
  order: Order;
  isDollar: boolean;
  language: Language;
  paymentProviderLabels: Record<PaymentProvider, string>;
  onDelete: (order: Order) => void;
}

const OrderRow = ({
  order,
  isDollar,
  language,
  paymentProviderLabels,
  onDelete,
}: OrderRowProps) => (
  <tr key={order.id} className="hover:bg-gray-50">
    <td className="px-6 py-4 text-sm text-gray-500">
      {convertToZone(format(new Date(order.created_at), "dd/MM/yyyy HH:mm"))}
    </td>
    <td
      className="px-6 py-4 text-sm text-blue-500 truncate"
      title={`${order.customer_name} ${order.customer_las_name}`}
    >
      {`${order.customer_name || ""} ${order.customer_las_name || ""}`}
    </td>
    <td className="px-6 py-4 text-sm text-gray-900">
      {formatCOP(Number(order.total_value_with_discount || 0), isDollar)}
    </td>
    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
      {order.payment_state === PaidState.PAID ? (
        <span className="px-3 py-1 text-sm rounded-full bg-green-100 text-green-800 flex items-center whitespace-nowrap">
          <span className="w-2 h-2 mr-2 bg-green-500 rounded-full flex-shrink-0"></span>
          {dictionary["paid-state"][language]}
        </span>
      ) : (
        <span className="px-3 py-1 text-sm rounded-full bg-red-100 text-red-800 flex items-center whitespace-nowrap">
          <span className="w-2 h-2 mr-2 bg-red-500 rounded-full flex-shrink-0"></span>
          {order.payment_state === "pending"
            ? dictionary["pending-state"][language]
            : dictionary["unpaid-state"][language]}
        </span>
      )}
    </td>
    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
      <span className="flex items-center px-3 py-1 text-sm rounded-full bg-[#5050EA]/15 text-[#5050EA] whitespace-nowrap">
        <span className="w-2 h-2 mr-2 bg-[#5050EA] rounded-full flex-shrink-0"></span>
        {(order.payment_provider &&
          paymentProviderLabels[order.payment_provider]) ||
          "Contra Entrega"}
      </span>
    </td>
    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
      {order.logistic_state === LogisticState.IN_PROGRESS ? (
        <span className="px-3 py-1 text-sm rounded-full bg-blue-100 text-blue-800 whitespace-nowrap">
          {dictionary["fulfilled-state"][language]}
        </span>
      ) : (
        <span className="px-3 py-1 text-sm rounded-full bg-yellow-100 text-yellow-800 whitespace-nowrap">
          {dictionary["unfulfilled-state"][language]}
        </span>
      )}
    </td>
    <td className="px-6 py-4 text-sm text-gray-500 flex items-center gap-4">
      <Link
        to={`/order/${order.hash_id}${location.search}`}
        className="text-blue-500"
      >
        {dictionary["view-details"]?.[language] || "Ver detalles"}
      </Link>
      <button onClick={() => onDelete(order)}>
        <IconTrash />
      </button>
    </td>
  </tr>
);

const SalesTable = ({
  filterOrders,
  orders,
  activeFilter,
  onOrderDeleted,
  onFilterByDate,
  activeDateFilter,
  onRemoveDateFilter,
  products,
  onFilterByProductId,
  activeProductFilters,
  onRemoveProductFilter,
}: SalesTableProps) => {
  const { language } = useAuth();
  const { isDollar } = useCurrency();
  const [isDownloading, setIsDownloading] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<Order | null>(null);
  const [isLoadingOrderDetails, setIsLoadingOrderDetails] = useState(false);
  const [deleteOrderMessage, setDeleteOrderMessage] = useState(
    dictionary["are-you-sure-you-want-to-delete-this-order"][language]
  );

  const paymentProviderLabels = {
    [PaymentProvider.CONTRA_ENTREGA]: "Contra Entrega",
    [PaymentProvider.MERCADO_PAGO]: "Mercado Pago",
    [PaymentProvider.STRIPE]: "Stripe",
  };

  const handleDownloadOrders = async () => {
    setIsDownloading(true);
    const orders = await downloadOrders();
    window.open(orders.s3_url, "_blank");
    setIsDownloading(false);
  };

  const handleDeleteOrder = async (order: Order) => {
    setOrderToDelete(order);
    setOpenModalDelete(true);
    setIsLoadingOrderDetails(true);
    const { res, error } = await handleApi(getOrderDetails(order.hash_id));
    if (error) {
      setDeleteOrderMessage(
        dictionary["error-getting-order-details"][language]
      );
      setIsLoadingOrderDetails(false);
      return;
    }
    
    const hasDropiIntegration = res?.order?.dropi_state;
    const hasShopifyIntegration = res?.order?.shopify_integration_state;
    
    let message = dictionary["are-you-sure-you-want-to-delete-this-order"][language];
    
    if (hasDropiIntegration && hasShopifyIntegration) {
      message = dictionary["this-order-is-integrated-with-dropi-and-shopify"][language];
    } else if (hasDropiIntegration) {
      message = dictionary["this-order-is-integrated-with-dropi"][language];
    } else if (hasShopifyIntegration) {
      message = dictionary["this-order-is-integrated-with-shopify"][language];
    }
    
    setDeleteOrderMessage(message);
    setIsLoadingOrderDetails(false);
  };

  const confirmDeleteOrder = async () => {
    if (!orderToDelete) return;
    setIsLoadingOrderDetails(true);
    const { error } = await handleApi(deleteOrder(orderToDelete.id));
    if (error) {
      toast.error(dictionary["error-deleting-order"][language]);
      setIsLoadingOrderDetails(false);
      return;
    }
    toast.success(dictionary["order-deleted"][language]);
    onOrderDeleted(orderToDelete.id.toString());
    setIsLoadingOrderDetails(false);
    setOpenModalDelete(false);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm">
      <DeleteOrderModal
        isOpen={openModalDelete}
        onClose={() => setOpenModalDelete(false)}
        message={deleteOrderMessage}
        onConfirm={confirmDeleteOrder}
        loading={isLoadingOrderDetails}
      />

      {/* filtros y botones */}
      <div className="flex justify-between items-center">
        <ButtonsFilters
          filterOrders={filterOrders}
          activeFilter={activeFilter}
        />
        <DefaultButton
          title={
            isDownloading
              ? dictionary["downloading"][language]
              : dictionary["download-orders"][language]
          }
          onClick={handleDownloadOrders}
          isLoading={isDownloading}
          className="mx-4"
        />
      </div>

      {/* filtro por fecha y producto */}
      <div className="mt-4 flex gap-4 px-4">
        <DateFilter onFilterApply={onFilterByDate} />
        <ProductFilter
          products={products}
          onProductSelect={onFilterByProductId}
        />
      </div>

      {/* etiquetas de filtros activos */}
      {(activeDateFilter || activeProductFilters.length > 0) && (
        <div className="mt-4 flex flex-wrap gap-2 px-4">
          {/* Filtro de fecha */}
          {activeDateFilter && (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E8EAFF] text-[#4D5FE8] rounded-full text-sm font-medium">
              <span>{activeDateFilter.label}</span>
              <button
                onClick={onRemoveDateFilter}
                className="hover:bg-[#4D5FE8] hover:text-white rounded-full p-0.5 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Filtros de productos */}
          {activeProductFilters.map((product: { id: string; name: string }) => (
            <div
              key={product.id}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#E2FBE8] text-[#3C7E45] rounded-full text-sm font-medium max-w-[250px]"
            >
              <span className="truncate">{product.name}</span>
              <button
                onClick={() => onRemoveProductFilter(product.id)}
                className="hover:bg-[#3C7E45] hover:text-white rounded-full p-0.5 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* tabla */}
      <div className="overflow-x-auto mt-4" style={{ minWidth: 0 }}>
        <table className="w-full" style={{ minWidth: "1000px" }}>
          <thead className="bg-gray-50">
            <tr>
              {[
                { key: "date-and-time", width: "160px" },
                { key: "customer", width: "180px" },
                { key: "total", width: "120px" },
                { key: "status", width: "140px" },
                { key: "payment-method", width: "160px" },
                { key: "fulfillment", width: "140px" },
                { key: "", width: "120px" },
              ].map((item, i) => (
                <th
                  key={i}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                  style={{ width: item.width }}
                >
                  {dictionary[item.key]?.[language] || ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {orders?.length === 0 ? (
              <EmptyState language={language} />
            ) : (
              orders.map((order) => (
                <OrderRow
                  key={order.id}
                  order={order}
                  isDollar={isDollar}
                  language={language}
                  paymentProviderLabels={paymentProviderLabels}
                  onDelete={handleDeleteOrder}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalesTable;
